import {
  Clock3,
  Mail,
  Search,
  ShoppingCart,
  Truck,
} from "lucide-react";
import type { FormEvent } from "react";
import type { PublicCategory, PublicCompany } from "@/types/store";
import styles from "./retail.module.css";

type Props = {
  company: PublicCompany;
  categories: PublicCategory[];
  search: string;
  attendanceLabel: string;
  catalogOpen: boolean;
  categoryId: number | null;
  onSearchChange: (value: string) => void;
  onSearch: (event: FormEvent<HTMLFormElement>) => void;
  onHome: () => void;
  onCatalog: () => void;
  onCategory: (id: number) => void;
  showCart: boolean;
  cartItemsCount: number;
  onOpenCart: () => void;
};

export default function RetailHeader({
  company,
  categories,
  search,
  attendanceLabel,
  catalogOpen,
  categoryId,
  onSearchChange,
  onSearch,
  onHome,
  onCatalog,
  onCategory,
  showCart,
  cartItemsCount,
  onOpenCart,
}: Props) {
  const delivery = company.local_delivery_enabled
    ? "Entrega local disponível"
    : company.pickup_enabled
      ? "Retirada disponível"
      : "";
  const information = [
    { text: delivery, Icon: Truck },
    { text: company.email, Icon: Mail },
  ].filter((item) => item.text);
  const secondaryLeft = information[0];
  const secondaryRight = information.length > 1 ? information.at(-1) : null;
  return (
    <>
      {(attendanceLabel || information.length > 0) && (
        <div
          className={`${styles.topbar} ${attendanceLabel ? styles.topbarWithStatus : ""}`}
        >
          <div className={styles.container}>
            {attendanceLabel ? (
              <>
                {secondaryLeft && (
                  <a href="#contato" className={styles.topbarLeft}>
                    <secondaryLeft.Icon size={14} aria-hidden="true" />
                    <span>{secondaryLeft.text}</span>
                  </a>
                )}
                <div className={styles.topbarStatus} title={attendanceLabel}>
                  <Clock3 size={14} aria-hidden="true" />
                  <span>{attendanceLabel}</span>
                </div>
                {secondaryRight && (
                  <a href="#contato" className={styles.topbarRight}>
                    <secondaryRight.Icon size={14} aria-hidden="true" />
                    <span>{secondaryRight.text}</span>
                  </a>
                )}
              </>
            ) : (
              information.map(({ text, Icon }) => (
                <a href="#contato" key={text}>
                  <Icon size={14} aria-hidden="true" />
                  <span>{text}</span>
                </a>
              ))
            )}
          </div>
        </div>
      )}
      <header className={styles.header}>
        <div className={`${styles.container} ${styles.headerRow}`}>
          <a className={styles.identity} href="#inicio" onClick={onHome}>
            {company.logo_url && (
              <img src={company.logo_url} alt="" decoding="async" />
            )}
            <h1>{company.name}</h1>
          </a>
          <form role="search" className={styles.search} onSubmit={onSearch}>
            <label htmlFor="retail-search" className={styles.srOnly}>
              Buscar produtos
            </label>
            <input
              id="retail-search"
              type="search"
              placeholder="O que você está procurando?"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            <button type="submit" aria-label="Buscar">
              <Search size={21} aria-hidden="true" />
            </button>
          </form>
          {showCart && (
            <button
              type="button"
              className={styles.cartButton}
              onClick={onOpenCart}
              aria-label={`Abrir carrinho, ${cartItemsCount} itens`}
            >
              <span className={styles.cartIcon}>
                <ShoppingCart size={26} aria-hidden="true" />
                <b>{cartItemsCount}</b>
              </span>
              <span>Meu carrinho</span>
            </button>
          )}
        </div>
        <nav className={styles.navigation} aria-label="Navegação da loja">
          <div className={styles.navLinks}>
            <a
              href="#inicio"
              onClick={onHome}
              aria-current={!catalogOpen ? "page" : undefined}
            >
              Início
            </a>
            <a
              href="#catalogo"
              onClick={onCatalog}
              aria-current={
                catalogOpen && categoryId === null ? "page" : undefined
              }
            >
              Produtos
            </a>
            {categories.map((category) => (
              <a
                key={category.id}
                href="#catalogo"
                onClick={() => onCategory(category.id)}
                aria-current={categoryId === category.id ? "page" : undefined}
              >
                {category.name}
              </a>
            ))}
            {company.description && <a href="#sobre">Sobre a empresa</a>}
            <a href="#contato">Contato</a>
          </div>
        </nav>
      </header>
    </>
  );
}
